import { UserPreview } from '@main/access-control-types';
import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

import { AirgapBundleInput } from './consentManager';
import { UiVariantStatus } from './enums';
import { ExperiencePreview } from './experience';

/** Details about a consent manager UI variant */
export const ConsentUiVariant = mkType({
  name: 'ConsentUiVariant',
  comment: 'A UI variant for consent manager',
  fields: {
    id: {
      comment: 'The id of the consent UI variant',
      type: 'id',
      modelName: 'consentUiVariant',
    },
    name: {
      comment: 'The display name for this UI variant',
      type: 'string',
    },
    slug: {
      comment: 'The unique identifier for this UI variant',
      type: 'string',
    },
    description: {
      comment: 'Description of this UI variant',
      type: 'string',
      optional: true,
    },
    locales: {
      comment: 'List of locales that this UI variant applies to',
      list: true,
      type: 'string', // Should be LOCALE_KEY enum but GraphQL does not allow hyphens in enums
    },
    // TODO: Replace configuration type once available - https://linear.app/transcend/issue/GOOM-134/update-ui-variant-config-type-once-available
    configuration: {
      comment: 'Configuration for this UI variant',
      type: 'string',
    },
    updatedBy: {
      comment: 'User who last updated this UI variant',
      type: UserPreview,
      optional: true,
    },
    status: {
      comment: 'Status of this UI variant',
      type: { UiVariantStatus },
    },
    experiences: {
      comment: 'Experiences that use this UI variant',
      type: ExperiencePreview,
      list: true,
      optional: true,
    },
    updatedAt: {
      comment: 'Date when this UI variant was last updated',
      type: 'string',
    },
  },
});

/** Override type */
export type ConsentUiVariant = SchemaToType<typeof ConsentUiVariant>;

export const ConsentUiVariantFilterInput = mkInput({
  name: 'ConsentUiVariantFilterInput',
  comment: 'Input used to filter consent UI variants',
  fields: {
    text: {
      comment:
        'Text to filter consent UI variants by name, slug, or description',
      type: 'string',
      optional: true,
    },
    status: {
      comment: 'Status of the consent UI variant',
      type: { UiVariantStatus },
      optional: true,
    },
    locales: {
      comment: 'List of locales to filter consent UI variants by',
      type: 'string', // Should be LOCALE_KEY enum but GraphQL does not allow hyphens in enums
      list: true,
      optional: true,
    },
    userId: {
      comment: 'User who last updated this consent UI variant',
      type: 'id',
      modelName: 'user',
      optional: true,
    },
  },
});

/** Override type */
export type ConsentUiVariantFilterInput = SchemaToType<
  typeof ConsentUiVariantFilterInput
>;

export const UpdateConsentUiVariantInput = mkInput({
  name: 'UpdateConsentUiVariantInput',
  comment: 'Input for updating a single consent UI variant',
  fields: {
    ...AirgapBundleInput.fields,
    id: {
      ...ConsentUiVariant.fields.id,
      comment: 'ID of the consent UI variant to update',
    },
    name: {
      ...ConsentUiVariant.fields.name,
      optional: true,
    },
    description: {
      ...ConsentUiVariant.fields.description,
      optional: true,
    },
    locales: {
      ...ConsentUiVariant.fields.locales,
      optional: true,
    },
    configuration: {
      ...ConsentUiVariant.fields.configuration,
      optional: true,
    },
    status: {
      ...ConsentUiVariant.fields.status,
      optional: true,
    },
  },
});

/** Override type */
export type UpdateConsentUiVariantInput = SchemaToType<
  typeof UpdateConsentUiVariantInput
>;

export const DeleteConsentUiVariantsInput = mkInput({
  name: 'DeleteConsentUiVariantsInput',
  comment: 'Input for deleting consent UI variants',
  fields: {
    ids: {
      comment: 'IDs of the consent UI variants to delete',
      type: 'id',
      modelName: 'consentUiVariant',
      list: true,
    },
  },
});

/** Override type */
export type DeleteConsentUiVariantsInput = SchemaToType<
  typeof DeleteConsentUiVariantsInput
>;

export const CreateConsentUiVariantInput = mkInput({
  name: 'CreateConsentUiVariantInput',
  comment: 'Input for creating a single consent UI variant',
  fields: {
    ...AirgapBundleInput.fields,
    name: {
      ...ConsentUiVariant.fields.name,
    },
    slug: {
      ...ConsentUiVariant.fields.slug,
    },
    description: {
      ...ConsentUiVariant.fields.description,
      optional: true,
    },
    locales: {
      ...ConsentUiVariant.fields.locales,
    },
    configuration: {
      ...ConsentUiVariant.fields.configuration,
    },
    status: {
      ...ConsentUiVariant.fields.status,
    },
  },
});

/** Override type */
export type CreateConsentUiVariantInput = SchemaToType<
  typeof CreateConsentUiVariantInput
>;
